import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import {
    addPortalTab,
    closeOtherPortalTabs,
    closePortalTab,
    closePortalTabsByType,
    closePortalTabsToSide,
    normalizePortalTabsState,
    openPortalTab,
    openPortalTabs,
    reorderPortalTabs,
    selectAdjacentPortalTab,
    selectPortalTab,
    setPortalTabTypeVisible,
    syncPortalTabRoute,
    type PortalTabSwitchingMode,
    type PortalTabType,
    type PortalTabsState,
} from './tabState'

type PortalTabsStore = PortalTabsState & {
    modalStudentNo: string | null
    openTab: (type: PortalTabType, resourceId: string, routePattern?: string) => void
    addTab: (type: PortalTabType, resourceId: string, routePattern?: string) => void
    openTabs: (type: PortalTabType, resourceIds: string[]) => void
    selectTab: (tabId: string) => void
    closeTab: (tabId: string) => void
    closeOtherTabs: (tabId: string, type?: PortalTabType) => void
    closeTabsToLeft: (tabId: string, type?: PortalTabType) => void
    closeTabsToRight: (tabId: string, type?: PortalTabType) => void
    closeTabsByType: (type?: PortalTabType) => void
    selectPreviousTab: () => void
    selectNextTab: () => void
    reorderTabs: (activeTabId: string, overTabId: string) => void
    syncRoute: (type: PortalTabType, resourceId: string, route: string) => void
    setTypeVisible: (type: PortalTabType, visible: boolean) => void
    setTabsVisible: (visible: boolean) => void
    toggleTabsVisible: () => void
    setTabsEnabled: (enabled: boolean) => void
    setColorfulTabs: (enabled: boolean) => void
    setShowTabIcons: (enabled: boolean) => void
    setTabColor: (type: PortalTabType, color: string) => void
    resetTabAppearance: () => void
    resetTabColors: () => void
    setSwitchingMode: (mode: PortalTabSwitchingMode) => void
    setNewStudentTabRoute: (route: string) => void
    resetTabBehavior: () => void
    openStudentModal: (studentNo: string) => void
    closeStudentModal: () => void
}

const initialTabsState: PortalTabsState = {
    tabs: [],
    selectedTabId: null,
    hiddenTypes: [],
    lastRouteByType: {},
    tabsVisible: true,
    tabsEnabled: true,
    colorfulTabs: true,
    showTabIcons: false,
    tabColors: { student: 'teal', class: 'violet', staff: 'blue' },
    switchingMode: 'shared',
    newStudentTabRoute: 'last',
}

export const usePortalTabsStore = create<PortalTabsStore>()(
    persist(
        (set) => ({
            ...initialTabsState,
            modalStudentNo: null,
            openTab: (type, resourceId, routePattern) => set((state) => openPortalTab(state, type, resourceId, routePattern)),
            addTab: (type, resourceId, routePattern) => set((state) => addPortalTab(state, type, resourceId, routePattern)),
            openTabs: (type, resourceIds) => set((state) => openPortalTabs(state, type, resourceIds)),
            selectTab: (tabId) => set((state) => selectPortalTab(state, tabId)),
            closeTab: (tabId) => set((state) => closePortalTab(state, tabId)),
            closeOtherTabs: (tabId, type) => set((state) => closeOtherPortalTabs(state, tabId, type)),
            closeTabsToLeft: (tabId, type) => set((state) => closePortalTabsToSide(state, tabId, 'left', type)),
            closeTabsToRight: (tabId, type) => set((state) => closePortalTabsToSide(state, tabId, 'right', type)),
            closeTabsByType: (type) => set((state) => closePortalTabsByType(state, type)),
            selectPreviousTab: () => set((state) => selectAdjacentPortalTab(state, -1)),
            selectNextTab: () => set((state) => selectAdjacentPortalTab(state, 1)),
            reorderTabs: (activeTabId, overTabId) => set((state) => reorderPortalTabs(state, activeTabId, overTabId)),
            syncRoute: (type, resourceId, route) => set((state) => syncPortalTabRoute(state, type, resourceId, route)),
            setTypeVisible: (type, visible) => set((state) => setPortalTabTypeVisible(state, type, visible)),
            setTabsVisible: (visible) => set({ tabsVisible: visible }),
            toggleTabsVisible: () => set((state) => ({ tabsVisible: !state.tabsVisible })),
            setTabsEnabled: (tabsEnabled) => set({ tabsEnabled }),
            setColorfulTabs: (colorfulTabs) => set({ colorfulTabs }),
            setShowTabIcons: (showTabIcons) => set({ showTabIcons }),
            setTabColor: (type, color) => set((state) => ({ tabColors: { ...state.tabColors, [type]: color } })),
            resetTabAppearance: () => set({ colorfulTabs: true, showTabIcons: false, tabColors: { student: 'teal', class: 'violet', staff: 'blue' } }),
            resetTabColors: () => set({ tabColors: { student: 'teal', class: 'violet', staff: 'blue' } }),
            setSwitchingMode: (switchingMode) => set({ switchingMode }),
            setNewStudentTabRoute: (newStudentTabRoute) => set({ newStudentTabRoute }),
            resetTabBehavior: () => set({ switchingMode: 'shared', newStudentTabRoute: 'last' }),
            openStudentModal: (studentNo) => set({ modalStudentNo: studentNo }),
            closeStudentModal: () => set({ modalStudentNo: null }),
        }),
        {
            name: 'portal-tabs-v1',
            version: 1,
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => normalizePortalTabsState({
                tabs: state.tabs,
                selectedTabId: state.selectedTabId,
                hiddenTypes: state.hiddenTypes,
                lastRouteByType: state.lastRouteByType,
                tabsVisible: state.tabsVisible,
                tabsEnabled: state.tabsEnabled,
                colorfulTabs: state.colorfulTabs,
                showTabIcons: state.showTabIcons,
                tabColors: state.tabColors,
                switchingMode: state.switchingMode,
                newStudentTabRoute: state.newStudentTabRoute,
            }),
            merge: (persisted, current) => ({
                ...current,
                ...normalizePortalTabsState(persisted as PortalTabsState),
                modalStudentNo: null,
            }),
        },
    ),
)
